import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import LockIcon from '@mui/icons-material/Lock';
import {
  fetchAudioBlobUrl,
  fetchPhoneTour,
  phoneTourPromptAudioPath,
} from '../mishtameretPortalApi';
import { examsPath } from '../nav';
import { portalPath } from '../stationMode';
import { formatHebrewDate } from '../../../utils/hebrewDate';

/**
 * סיור במערכת הטלפונית — לוח מקשים כמו בטלפון: לוחצים על מקש ונכנסים לשלוחה,
 * ולכל שלוחה רואים למה היא משמשת, מה יש בה עכשיו, מה שומעים בה ואיך מתנהלת השיחה.
 * המיקום נשמר ב-?at=2/1 — כפתור "אחורה" בדפדפן עובד, ומקשי המקלדת (0–9, #) פועלים כמו בטלפון.
 */
const KEYPAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

const KIND_LABELS = {
  menu: 'תפריט',
  lessons: 'שיעורים',
  exams: 'מבחנים',
  grades: 'ציונים',
  record: 'הקלטה',
  order: 'הזמנה',
  stub: 'בקרוב',
  restricted: 'גישה מוגבלת',
};

const EXAM_STATUS = {
  completed: (e) => `נבחנתם · ציון ${e.score}`,
  in_progress: () => 'התחלתם — אפשר להמשיך',
};

function findNode(node, path) {
  if (!node) return null;
  if (node.path === path) return node;
  for (const c of node.children || []) {
    if (path === c.path || path.startsWith(`${c.path}/`)) return findNode(c, path);
  }
  return null;
}

function parentPath(path) {
  return path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '';
}

/** שורת סיכום קצרה לכרטיס אפשרות */
function summarize(node) {
  const live = node.live;
  if (live?.type === 'lessons') return live.count ? `${live.count} שיעורים` : 'אין שיעורים כרגע';
  if (live?.type === 'exams') return live.exams.length ? `${live.exams.length} מבחנים פתוחים` : 'אין מבחן פתוח כרגע';
  if (node.kind === 'menu' && node.children?.length) return `${node.children.length} אפשרויות`;
  return KIND_LABELS[node.kind] || '';
}

const Section = ({ theme, title, children }) => (
  <Box sx={{ mt: 2.5 }}>
    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: theme.textMuted, mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const Card = ({ theme, children, sx }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: `${theme.radius}px`,
      border: `1px solid ${theme.borderSoft}`,
      bgcolor: theme.surface,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const KeyBadge = ({ theme, children }) => (
  <Box
    component="span"
    sx={{
      flexShrink: 0,
      minWidth: 36,
      height: 36,
      px: 1,
      borderRadius: 999,
      display: 'inline-grid',
      placeItems: 'center',
      fontWeight: 700,
      fontSize: '0.95rem',
      bgcolor: theme.accent,
      color: theme.accentContrast,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Box>
);

const Keypad = ({ theme, node, isRoot, onPress }) => {
  const byDigit = Object.fromEntries((node.children || []).map((c) => [c.digit, c]));
  return (
    <Box sx={{ direction: 'ltr', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25 }}>
      {KEYPAD.map((key) => {
        const child = byDigit[key];
        let label = child?.label || '';
        if (key === '0' && !isRoot) label = 'חזרה';
        if (key === '#') label = 'מקום אחרון';
        const enabled = Boolean(child) || key === '#' || (key === '0' && !isRoot);
        return (
          <Box
            key={key}
            component="button"
            type="button"
            disabled={!enabled}
            onClick={() => onPress(key)}
            aria-label={label ? `${key} — ${label}` : key}
            sx={{
              direction: 'rtl',
              aspectRatio: '1.15',
              minHeight: 64,
              borderRadius: 3,
              border: `1px solid ${enabled ? theme.border : theme.borderSoft}`,
              bgcolor: enabled ? theme.surface : 'transparent',
              color: enabled ? theme.text : theme.textMuted,
              opacity: enabled ? 1 : 0.4,
              cursor: enabled ? 'pointer' : 'default',
              fontFamily: theme.fontBody,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.25,
              px: 0.5,
              transition: 'background 0.15s, transform 0.08s, border-color 0.15s',
              '&:hover:enabled': { bgcolor: theme.surfaceHover, borderColor: theme.accent },
              '&:active:enabled': { transform: 'scale(0.95)', bgcolor: theme.accent, color: theme.accentContrast },
              '&:focus-visible': { outline: `2px solid ${theme.accent}`, outlineOffset: 2 },
            }}
          >
            <Box component="span" sx={{ fontSize: '1.6rem', fontWeight: 700, lineHeight: 1 }}>
              {key}
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: '0.68rem',
                lineHeight: 1.15,
                color: 'inherit',
                opacity: 0.75,
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minHeight: '0.8rem',
              }}
            >
              {label}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const LiveInfo = ({ theme, node, isStation, navigate }) => {
  const live = node.live;
  if (!live) return null;

  if (live.type === 'intro') {
    return (
      <Card theme={theme}>
        {live.introCount
          ? `לפני התפריט מושמעות ${live.introCount === 1 ? 'הודעת פתיח אחת' : `${live.introCount} הודעות פתיח`} — כל מקש מדלג עליהן.`
          : 'אין כרגע הודעת פתיח — השיחה נפתחת ישר בברכה ובתפריט.'}
      </Card>
    );
  }

  if (live.type === 'lessons') {
    if (!live.count) {
      return <Card theme={theme}>אין כרגע שיעורים בשלוחה. המאזין שומע "אין שיעורים זמינים כרגע" וחוזר לתפריט.</Card>;
    }
    const n = live.newest || {};
    return (
      <Card theme={theme}>
        <Typography sx={{ color: theme.textMuted, fontSize: '0.85rem' }}>
          {live.count} שיעורים · השיעור שמתנגן ראשון:
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', mt: 0.5 }}>{n.title || 'שיעור'}</Typography>
        <Typography sx={{ color: theme.textMuted, fontSize: '0.9rem', mt: 0.25 }}>
          {[n.speaker, n.date ? formatHebrewDate(n.date) : ''].filter(Boolean).join(' · ')}
        </Typography>
        {!isStation ? (
          <Button
            onClick={() => navigate(portalPath(false, `/library/${encodeURIComponent(node.channel)}`))}
            sx={{ mt: 1.5, color: theme.accent, fontWeight: 700, px: 0 }}
          >
            לשמיעת השיעורים כאן בפורטל ←
          </Button>
        ) : null}
      </Card>
    );
  }

  if (live.type === 'exams') {
    return (
      <Card theme={theme}>
        {!live.exams.length ? (
          <Typography>אין כרגע מבחן פתוח בשלוחה. המאזין שומע "אין מבחנים פתוחים כרגע".</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {live.exams.map((e) => (
              <Box key={e.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <KeyBadge theme={theme}>{e.key}</KeyBadge>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700 }}>{e.title}</Typography>
                  <Typography sx={{ color: theme.textMuted, fontSize: '0.85rem' }}>
                    {e.slot} · {(EXAM_STATUS[e.status] || (() => 'טרם נבחנתם'))(e)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
        <Button
          onClick={() => navigate(examsPath(node.frame, isStation))}
          sx={{ mt: 1.5, color: theme.accent, fontWeight: 700, px: 0 }}
        >
          להיבחן כאן בפורטל ←
        </Button>
      </Card>
    );
  }

  if (live.type === 'grades') {
    return (
      <Card theme={theme}>
        {live.last ? (
          <>
            <Typography sx={{ color: theme.textMuted, fontSize: '0.85rem' }}>
              {live.count} ציונים שמורים · האחרון שיוקרא:
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', mt: 0.5 }}>
              {live.last.title} — {live.last.score}
            </Typography>
          </>
        ) : (
          <Typography>עדיין אין לכם ציונים. המאזין שומע "לא נמצאו ציונים במערכת".</Typography>
        )}
        <Button
          onClick={() => navigate(portalPath(isStation, '/grades'))}
          sx={{ mt: 1.5, color: theme.accent, fontWeight: 700, px: 0 }}
        >
          לכל המבחנים שלי ←
        </Button>
      </Card>
    );
  }
  return null;
};

const PhoneTourScreen = () => {
  const { theme, isStation = false } = useOutletContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [globalKey, setGlobalKey] = useState(null);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);
  const stopRef = useRef(null);
  const seqRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchPhoneTour();
        if (!cancelled) setTour(data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'שגיאה בטעינת מפת הקו');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const at = searchParams.get('at') || '';
  const node = useMemo(() => (tour ? findNode(tour.root, at) || tour.root : null), [tour, at]);
  const isRoot = !node?.path;

  const stopAudio = useCallback(() => {
    seqRef.current += 1;
    audioRef.current?.pause();
    audioRef.current = null;
    stopRef.current?.();
    setPlaying(null);
  }, []);

  /** משמיע את ההקלטות ברצף, כמו שהמאזין שומע אותן; לחיצה נוספת / מעבר שלוחה עוצרים */
  const playCodes = useCallback(async (codes) => {
    stopAudio();
    const seq = seqRef.current;
    for (const code of codes) {
      if (seqRef.current !== seq) return;
      setPlaying(code);
      let url = null;
      try {
        url = await fetchAudioBlobUrl(phoneTourPromptAudioPath(code));
        if (seqRef.current !== seq) return;
        await new Promise((resolve) => {
          const audio = new Audio(url);
          audioRef.current = audio;
          stopRef.current = resolve;
          audio.onended = resolve;
          audio.onerror = resolve;
          audio.play().catch(resolve);
        });
      } catch {
        /* אין קובץ — ממשיכים לבא */
      } finally {
        if (url) URL.revokeObjectURL(url);
      }
    }
    if (seqRef.current === seq) setPlaying(null);
  }, [stopAudio]);

  useEffect(() => stopAudio, [node, globalKey, stopAudio]);

  const goTo = useCallback((path) => {
    setGlobalKey(null);
    setSearchParams(path ? { at: path } : {});
  }, [setSearchParams]);

  const press = useCallback((key) => {
    if (!node) return;
    if (key === '#') {
      setGlobalKey('#');
      return;
    }
    if (key === '0') {
      if (!isRoot) goTo(parentPath(node.path));
      return;
    }
    const child = (node.children || []).find((c) => c.digit === key);
    if (child) goTo(child.path);
  }, [node, isRoot, goTo]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName || '')) return;
      if (KEYPAD.includes(e.key)) press(e.key);
      else if (e.key === 'Backspace') press('0');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [press]);

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
        <CircularProgress sx={{ color: theme.accent }} />
      </Box>
    );
  }
  if (!node) {
    return <Typography sx={{ color: theme.danger }}>{error || 'מפת הקו אינה זמינה'}</Typography>;
  }

  const trail = [tour.root];
  if (node.path) {
    node.path.split('/').forEach((_, i, parts) => {
      const hit = findNode(tour.root, parts.slice(0, i + 1).join('/'));
      if (hit) trail.push(hit);
    });
  }
  const shown = globalKey ? tour.globalKeys.find((k) => k.digit === globalKey) : null;
  const prompts = (shown || node).prompts || [];
  const playable = prompts.filter((p) => p.hasAudio).map((p) => p.code);

  return (
    <Box>
      <Typography sx={{ fontFamily: theme.fontDisplay, fontWeight: 700, fontSize: { xs: '1.3rem', sm: '1.5rem' } }}>
        סיור במערכת הטלפונית
      </Typography>
      <Typography sx={{ color: theme.textMuted, mt: 0.5, mb: 2.5 }}>
        לחצו על מקש כדי להיכנס לשלוחה — בדיוק כמו בשיחה לקו. אפשר גם להקיש במקלדת; 0 חוזר אחורה.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '300px 1fr' },
          gap: { xs: 2.5, md: 3.5 },
          alignItems: 'start',
        }}
      >
        {/* הטלפון */}
        <Box
          sx={{
            position: { md: 'sticky' },
            top: { md: 150 },
            p: 2,
            borderRadius: 5,
            border: `1px solid ${theme.border}`,
            bgcolor: theme.surface,
            boxShadow: theme.shadow,
            maxWidth: 340,
            width: '100%',
            mx: 'auto',
          }}
        >
          <Box
            aria-live="polite"
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 3,
              bgcolor: theme.inputBg,
              border: `1px solid ${theme.borderSoft}`,
              minHeight: 78,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: theme.accent }}>
              <PhoneInTalkIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, direction: 'ltr' }}>
                {node.path ? node.path.split('/').join(' › ') : 'שיחה נכנסת'}
              </Typography>
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', mt: 0.75, lineHeight: 1.3 }}>
              {shown ? shown.title : node.title}
            </Typography>
          </Box>
          <Keypad theme={theme} node={node} isRoot={isRoot} onPress={press} />
        </Box>

        {/* פירוט השלוחה */}
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mb: 1 }}>
            {trail.map((t, i) => (
              <React.Fragment key={t.path || 'root'}>
                {i > 0 ? <Typography sx={{ color: theme.textMuted, fontSize: '0.85rem' }}>‹</Typography> : null}
                <Box
                  component="button"
                  type="button"
                  onClick={() => goTo(t.path)}
                  disabled={i === trail.length - 1 && !shown}
                  sx={{
                    border: 0,
                    bgcolor: 'transparent',
                    p: 0.25,
                    fontFamily: theme.fontBody,
                    fontSize: '0.85rem',
                    color: i === trail.length - 1 && !shown ? theme.text : theme.accent,
                    fontWeight: i === trail.length - 1 ? 700 : 500,
                    cursor: 'pointer',
                    '&:disabled': { cursor: 'default' },
                  }}
                >
                  {t.digit ? `${t.digit} · ${t.label}` : 'ראשי'}
                </Box>
              </React.Fragment>
            ))}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
            <Typography sx={{ fontFamily: theme.fontDisplay, fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.45rem' } }}>
              {shown ? `${shown.digit} · ${shown.title}` : node.title}
            </Typography>
            {!shown ? (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.25,
                  py: 0.25,
                  borderRadius: 999,
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  border: `1px solid ${theme.border}`,
                  color: theme.accent,
                }}
              >
                {node.kind === 'restricted' ? <LockIcon sx={{ fontSize: 14 }} /> : null}
                {KIND_LABELS[node.kind]}
              </Box>
            ) : null}
          </Box>

          <Typography sx={{ mt: 1, lineHeight: 1.7 }}>{(shown || node).about}</Typography>

          {!shown && node.live ? (
            <Section theme={theme} title="מה יש עכשיו">
              <LiveInfo theme={theme} node={node} isStation={isStation} navigate={navigate} />
            </Section>
          ) : null}

          {prompts.length ? (
            <Section theme={theme} title="מה שומעים">
              <Card theme={theme} sx={{ p: 1 }}>
                {playable.length > 1 ? (
                  <Button
                    size="small"
                    startIcon={playing ? <StopCircleIcon /> : <VolumeUpIcon />}
                    onClick={() => (playing ? stopAudio() : playCodes(playable))}
                    sx={{ color: theme.accent, fontWeight: 700, mb: 0.5, mx: 1 }}
                  >
                    {playing ? 'עצירה' : 'השמעת הכל ברצף'}
                  </Button>
                ) : null}
                {prompts.map((p, i) => {
                  const isPlaying = playing && playing === p.code;
                  return (
                    <Box
                      key={`${p.code || 'tts'}-${i}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1,
                        py: 0.75,
                        borderRadius: 2,
                        bgcolor: isPlaying ? theme.surfaceHover : 'transparent',
                      }}
                    >
                      {p.hasAudio ? (
                        <IconButton
                          size="small"
                          aria-label={isPlaying ? 'עצירה' : 'השמעה'}
                          onClick={() => (isPlaying ? stopAudio() : playCodes([p.code]))}
                          sx={{ color: theme.accent }}
                        >
                          {isPlaying ? <StopCircleIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
                        </IconButton>
                      ) : (
                        <Box sx={{ width: 34, flexShrink: 0 }} />
                      )}
                      <Typography sx={{ flex: 1, lineHeight: 1.5 }}>“{p.text}”</Typography>
                      {!p.code ? (
                        <Typography sx={{ color: theme.textMuted, fontSize: '0.75rem', flexShrink: 0 }}>
                          קול ממוחשב
                        </Typography>
                      ) : null}
                    </Box>
                  );
                })}
              </Card>
            </Section>
          ) : null}

          {!shown && node.steps?.length ? (
            <Section theme={theme} title="איך זה עובד">
              <Card theme={theme} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {node.steps.map((s, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <KeyBadge theme={theme}>{s.keys}</KeyBadge>
                    <Typography sx={{ lineHeight: 1.6, pt: 0.6 }}>{s.text}</Typography>
                  </Box>
                ))}
              </Card>
            </Section>
          ) : null}

          {!shown && node.children?.length ? (
            <Section theme={theme} title="האפשרויות בשלוחה">
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
                {node.children.map((c) => (
                  <Box
                    key={c.path}
                    component="button"
                    type="button"
                    onClick={() => goTo(c.path)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      textAlign: 'start',
                      borderRadius: `${theme.radius}px`,
                      border: `1px solid ${theme.borderSoft}`,
                      bgcolor: theme.surface,
                      color: theme.text,
                      fontFamily: theme.fontBody,
                      cursor: 'pointer',
                      transition: 'border-color 0.15s, background 0.15s',
                      '&:hover': { borderColor: theme.accent, bgcolor: theme.surfaceHover },
                    }}
                  >
                    <KeyBadge theme={theme}>{c.digit}</KeyBadge>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, lineHeight: 1.35 }}>{c.title}</Typography>
                      <Typography sx={{ color: theme.textMuted, fontSize: '0.85rem', mt: 0.25 }}>
                        {summarize(c)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Section>
          ) : null}

          {!shown && isRoot ? (
            <Section theme={theme} title="מקשים שעובדים בכל מקום">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {tour.globalKeys.map((k) => (
                  <Card key={k.digit} theme={theme} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5 }}>
                    <KeyBadge theme={theme}>{k.digit}</KeyBadge>
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>{k.title}</Typography>
                      <Typography sx={{ color: theme.textMuted, fontSize: '0.9rem' }}>{k.about}</Typography>
                    </Box>
                  </Card>
                ))}
              </Box>
            </Section>
          ) : null}

          {shown ? (
            <Button onClick={() => setGlobalKey(null)} sx={{ mt: 2.5, color: theme.accent, fontWeight: 700, px: 0 }}>
              ← חזרה ל{node.title}
            </Button>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default PhoneTourScreen;
